import { useLayoutEffect, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import type { StripeEmbeddedCheckout } from "@stripe/stripe-js";
import { Modal, ModalBody, ModalHeader } from "components/common/modal";

export type EmbeddedStudioPurchaseModalProps = {
  isOpen: boolean;
  clientSecret: string;
  publishableKey: string;
  onClose: () => void;
  onComplete: () => void;
};

export const EmbeddedStudioPurchaseModal = ({
  isOpen,
  clientSecret,
  publishableKey,
  onClose,
  onComplete,
}: EmbeddedStudioPurchaseModalProps) => {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const checkoutRef = useRef<StripeEmbeddedCheckout | null>(null);
  const onCompleteRef = useRef(onComplete);
  const onCloseRef = useRef(onClose);
  onCompleteRef.current = onComplete;
  onCloseRef.current = onClose;

  useLayoutEffect(() => {
    if (!isOpen || !clientSecret?.trim() || !publishableKey?.trim() || !mountRef.current) {
      return undefined;
    }

    const mountEl = mountRef.current;
    let cancelled = false;

    void (async () => {
      const stripe = await loadStripe(publishableKey);
      if (!stripe || cancelled) {
        return;
      }

      try {
        const checkout = await stripe.initEmbeddedCheckout({
          clientSecret,
          onComplete: () => {
            try {
              checkout.destroy();
            } catch {
              /* ignore */
            }
            checkoutRef.current = null;
            onCompleteRef.current();
          },
        });
        if (cancelled) {
          try {
            checkout.destroy();
          } catch {
            /* ignore */
          }
          return;
        }
        checkoutRef.current = checkout;
        checkout.mount(mountEl);
      } catch {
        onCloseRef.current();
      }
    })();

    return () => {
      cancelled = true;
      try {
        checkoutRef.current?.destroy();
      } catch {
        /* ignore */
      }
      checkoutRef.current = null;
    };
  }, [isOpen, clientSecret, publishableKey]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-3xl"
      backdropClassName="bg-black/60 backdrop-blur-sm"
      className="border border-border bg-card shadow-2xl"
    >
      <ModalHeader
        title="Complete purchase"
        subtitle="Secure checkout by Stripe"
        onClose={onClose}
        className="border-b border-border"
      />
      <ModalBody className="p-4 sm:p-6">
        <div ref={mountRef} className="min-h-[28rem] w-full" />
      </ModalBody>
    </Modal>
  );
};
